import {
    Badge,
    Button,
    Drawer,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Table,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { TranslationStatusEnum } from '@routellers/types/dist/translation/enum/translation-status.enum';
import { NamespaceRO } from '@routellers/types/dist/translation/namespace.ro';
import { TranslationRO } from '@routellers/types/dist/translation/translation.ro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { enumLabel } from '../i18n';
import { LIST_PAGE_SIZE } from '../utils/list';

const statusColor: Record<TranslationStatusEnum, string> = {
    [TranslationStatusEnum.NOT_READY]: 'gray',
    [TranslationStatusEnum.WAITING]: 'blue',
    [TranslationStatusEnum.NEED_CHECK]: 'orange',
    [TranslationStatusEnum.READY]: 'green',
    [TranslationStatusEnum.SKIPPER]: 'gray',
};

export const TranslationsPage = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<TranslationRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const [namespaceId, setNamespaceId] = useState<string | null>(null);
    const [namespaces, setNamespaces] = useState<NamespaceRO[]>([]);
    const [busy, setBusy] = useState<'generate' | 'sync' | null>(null);

    const [selected, setSelected] = useState<TranslationRO | null>(null);
    const [localeDrafts, setLocaleDrafts] = useState<Record<number, string>>({});
    const [savingLocaleId, setSavingLocaleId] = useState<number | null>(null);

    const [createKeyOpen, setCreateKeyOpen] = useState(false);
    const [createNsOpen, setCreateNsOpen] = useState(false);
    const [newKey, setNewKey] = useState({ row: '', text: '', context: '', namespaceId: '' });
    const [newNsTitle, setNewNsTitle] = useState('');
    const [creating, setCreating] = useState(false);

    const loadNamespaces = useCallback(async () => {
        try {
            const { data } = await api.translationsApi.getAllNamespaces(undefined);
            setNamespaces(data || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            // API `@IsNotEmpty()` rejects `{}` — keep a sentinel when no namespace filter.
            const where: Record<string, unknown> = namespaceId
                ? { namespaceId: Number(namespaceId) }
                : { namespaceId: null };

            const { data } = await api.translationsApi.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: searchApplied.trim() || undefined,
                sort: 'row',
                order: 'ASC',
                where,
            });

            setRows(data?.items || []);
            setTotal(Number(data?.meta?.totalItems || data?.items?.length || 0));
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, searchApplied, namespaceId]);

    useEffect(() => {
        loadNamespaces();
    }, [loadNamespaces]);

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setPage(1);
            setSearchApplied(search);
        }, 300);

        return () => window.clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        load();
    }, [load]);

    const namespaceOptions = useMemo(
        () => namespaces.map((ns) => ({ value: String(ns.id), label: ns.title })),
        [namespaces],
    );

    const openRow = (row: TranslationRO) => {
        setSelected(row);
        const drafts: Record<number, string> = {};
        for (const loc of row.translations || []) {
            drafts[loc.id] = loc.text || '';
        }
        setLocaleDrafts(drafts);
    };

    const saveLocale = async (localeId: number) => {
        const text = (localeDrafts[localeId] || '').trim();
        if (!text) {
            notifications.show({
                color: 'red',
                message: t('pages.translations.localeTextRequired'),
            });
            return;
        }

        setSavingLocaleId(localeId);
        try {
            const { data } = await api.translationsApi.updateLocale({
                id: localeId,
                data: { text },
            });
            if (data) {
                setSelected(data);
                const drafts: Record<number, string> = {};
                for (const loc of data.translations || []) {
                    drafts[loc.id] = loc.text || '';
                }
                setLocaleDrafts(drafts);
                setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
            }
            notifications.show({ color: 'green', message: t('pages.translations.localeSaved') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSavingLocaleId(null);
        }
    };

    const generateJson = async () => {
        setBusy('generate');
        try {
            await api.translationsApi.generateJson(undefined);
            notifications.show({ color: 'green', message: t('pages.translations.jsonGenerated') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusy(null);
        }
    };

    const syncFromCdn = async () => {
        setBusy('sync');
        try {
            const { data } = await api.translationsApi.syncFromCdn(undefined);
            const stats =
                data && typeof data === 'object'
                    ? `ok=${data.ok ?? '?'} missing=${data.missing ?? '?'}`
                    : '';
            notifications.show({
                color: 'green',
                message: stats
                    ? t('pages.translations.syncedFromCdnStats', { stats })
                    : t('pages.translations.syncedFromCdn'),
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusy(null);
        }
    };

    const createNamespace = async () => {
        if (!newNsTitle.trim()) {
            notifications.show({
                color: 'red',
                message: t('pages.translations.namespaceTitleRequired'),
            });
            return;
        }
        setCreating(true);
        try {
            await api.translationsApi.createNamespace({ data: { title: newNsTitle.trim() } });
            notifications.show({
                color: 'green',
                message: t('pages.translations.namespaceCreated'),
            });
            setCreateNsOpen(false);
            setNewNsTitle('');
            await loadNamespaces();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setCreating(false);
        }
    };

    const createKey = async () => {
        if (!newKey.row.trim() || !newKey.text.trim() || !newKey.namespaceId) {
            notifications.show({
                color: 'red',
                message: t('pages.translations.keyRequiredFields'),
            });
            return;
        }
        setCreating(true);
        try {
            await api.translationsApi.createTranslation({
                data: {
                    row: newKey.row.trim(),
                    text: newKey.text.trim(),
                    context: newKey.context.trim() || undefined,
                    namespaceId: Number(newKey.namespaceId),
                },
            });
            notifications.show({
                color: 'green',
                message: t('pages.translations.translationKeyCreated'),
            });
            setCreateKeyOpen(false);
            setNewKey({ row: '', text: '', context: '', namespaceId: '' });
            setPage(1);
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setCreating(false);
        }
    };

    const filledCount = (row: TranslationRO) =>
        (row.translations || []).filter(
            (loc) => loc.text?.trim() && loc.status === TranslationStatusEnum.READY,
        ).length;

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.translations.title')}
                action={
                    <Group gap='xs'>
                        <Button variant='default' loading={busy === 'sync'} onClick={syncFromCdn}>
                            {t('pages.translations.syncCdn')}
                        </Button>
                        <Button
                            variant='light'
                            loading={busy === 'generate'}
                            onClick={generateJson}
                        >
                            {t('pages.translations.generateJson')}
                        </Button>
                        <Button variant='default' onClick={() => setCreateNsOpen(true)}>
                            {t('pages.translations.namespace')}
                        </Button>
                        <Button onClick={() => setCreateKeyOpen(true)}>
                            {t('pages.translations.createKey')}
                        </Button>
                    </Group>
                }
            />

            <Group>
                <TextInput
                    placeholder={t('pages.translations.searchPlaceholder')}
                    value={search}
                    onChange={(e) => {
                        setPage(1);
                        setSearch(e.currentTarget.value);
                    }}
                    w={260}
                />
                <Select
                    placeholder={t('pages.translations.allNamespaces')}
                    clearable
                    searchable
                    data={namespaceOptions}
                    value={namespaceId}
                    onChange={(v) => {
                        setPage(1);
                        setNamespaceId(v);
                    }}
                    w={220}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.translations.columns.key')}</Table.Th>
                                    <Table.Th>{t('pages.translations.columns.namespace')}</Table.Th>
                                    <Table.Th>{t('pages.translations.columns.context')}</Table.Th>
                                    <Table.Th ta='right'>
                                        {t('pages.translations.columns.locales')}
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => openRow(row)}
                                    >
                                        <Table.Td>
                                            <EntityThumb
                                                src={row.photo}
                                                name={row.row}
                                                subtitle={row.namespace?.title}
                                                radius='md'
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge variant='light'>{row.namespace?.title}</Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size='sm' c='dimmed' lineClamp={2}>
                                                {row.context || t('pages.translations.noContext')}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td ta='right'>
                                            <Text size='sm'>
                                                {filledCount(row)}/{row.translations?.length || 0}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!rows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={4}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.translations.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    <ListPagination page={page} totalItems={total} onChange={setPage} />
                </>
            )}

            <Drawer
                opened={selected != null}
                onClose={() => setSelected(null)}
                title={
                    selected
                        ? `${selected.namespace?.title}.${selected.row}`
                        : t('pages.translations.title')
                }
                position='right'
                size='lg'
            >
                {selected && (
                    <Stack gap='md'>
                        <Text size='sm' c='dimmed'>
                            {selected.context || t('pages.translations.noContext')}
                        </Text>
                        {(selected.translations || []).map((loc) => (
                            <Stack key={loc.id} gap='xs'>
                                <Group justify='space-between'>
                                    <Text size='sm' fw={600}>
                                        {String(loc.locale).toUpperCase()}
                                    </Text>
                                    <Badge
                                        size='sm'
                                        variant='light'
                                        color={statusColor[loc.status] || 'gray'}
                                    >
                                        {enumLabel(t, 'enums.translationStatus', loc.status)}
                                    </Badge>
                                </Group>
                                <Textarea
                                    minRows={2}
                                    autosize
                                    value={localeDrafts[loc.id] ?? ''}
                                    onChange={(e) => {
                                        const text = e.currentTarget.value;
                                        setLocaleDrafts((d) => ({
                                            ...d,
                                            [loc.id]: text,
                                        }));
                                    }}
                                />
                                <Button
                                    size='xs'
                                    variant='light'
                                    loading={savingLocaleId === loc.id}
                                    onClick={() => saveLocale(loc.id)}
                                    w='fit-content'
                                >
                                    {t('pages.translations.saveLocale')}
                                </Button>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Drawer>

            <Modal
                opened={createKeyOpen}
                onClose={() => setCreateKeyOpen(false)}
                title={t('pages.translations.createKeyModal.title')}
                centered
            >
                <Stack gap='md'>
                    <Select
                        label={t('pages.translations.createKeyModal.namespace')}
                        required
                        searchable
                        data={namespaceOptions}
                        value={newKey.namespaceId || null}
                        onChange={(v) => setNewKey((k) => ({ ...k, namespaceId: v || '' }))}
                    />
                    <TextInput
                        label={t('pages.translations.createKeyModal.key')}
                        required
                        value={newKey.row}
                        onChange={(e) => {
                            const row = e.currentTarget.value;
                            setNewKey((k) => ({ ...k, row }));
                        }}
                    />
                    <Textarea
                        label={t('pages.translations.createKeyModal.sourceText')}
                        required
                        minRows={2}
                        value={newKey.text}
                        onChange={(e) => {
                            const text = e.currentTarget.value;
                            setNewKey((k) => ({ ...k, text }));
                        }}
                    />
                    <Textarea
                        label={t('pages.translations.createKeyModal.context')}
                        minRows={2}
                        value={newKey.context}
                        onChange={(e) => {
                            const context = e.currentTarget.value;
                            setNewKey((k) => ({ ...k, context }));
                        }}
                    />
                    <Button loading={creating} onClick={createKey}>
                        {t('pages.translations.createKeyModal.create')}
                    </Button>
                </Stack>
            </Modal>

            <Modal
                opened={createNsOpen}
                onClose={() => setCreateNsOpen(false)}
                title={t('pages.translations.createNamespaceModal.title')}
                centered
            >
                <Stack gap='md'>
                    <TextInput
                        label={t('pages.translations.createNamespaceModal.titleField')}
                        required
                        value={newNsTitle}
                        onChange={(e) => setNewNsTitle(e.currentTarget.value)}
                    />
                    <Button loading={creating} onClick={createNamespace}>
                        {t('pages.translations.createNamespaceModal.create')}
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
};
